import React, { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../api/config";
import "./KiAdmin.css";

const API = `${API_BASE}/ki/admin/fragen`;

const STATUS_LABELS = {
  offen:       { label: "Offen",       emoji: "🔔", cls: "status-offen" },
  beantwortet: { label: "Beantwortet", emoji: "✅", cls: "status-beantwortet" },
  ignoriert:   { label: "Ignoriert",   emoji: "🚫", cls: "status-ignoriert" },
};

function ZeitAgo({ ts }) {
  if (!ts) return null;
  const d = new Date(ts);
  return (
    <span title={d.toLocaleString("de-DE")}>
      {d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}{" "}
      {d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
    </span>
  );
}

export default function KiAdmin() {
  const [fragen, setFragen]     = useState([]);
  const [filter, setFilter]     = useState("offen");
  const [loading, setLoading]   = useState(false);
  const [antworten, setAntworten] = useState({}); // { id: text }
  const [pending, setPending]   = useState(null);  // id der gerade gespeichert wird
  const [wissen, setWissen]     = useState("");
  const [wissenOffen, setWissenOffen] = useState(false);

  const laden = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === "alle" ? API : `${API}?status=${filter}`;
      const resp = await fetch(url);
      const data = await resp.json();
      setFragen(data.fragen || []);
    } catch {
      setFragen([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { laden(); }, [laden]);

  // Wissen.md laden
  useEffect(() => {
    fetch(`${API_BASE}/ki/admin/wissen`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setWissen(d.inhalt || ""))
      .catch(() => {});
  }, []);

  const beantworten = async (fid) => {
    const antwort = (antworten[fid] || "").trim();
    if (!antwort) return;
    setPending(fid);
    try {
      const resp = await fetch(`${API_BASE}/ki/admin/fragen/${fid}/antworten`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ antwort }),
      });
      if (resp.ok) {
        setAntworten(prev => ({ ...prev, [fid]: "" }));
        await laden();
        // wissen.md neu laden
        fetch(`${API_BASE}/ki/admin/wissen`)
          .then(r => r.ok ? r.json() : null)
          .then(d => d && setWissen(d.inhalt || ""))
          .catch(() => {});
      }
    } finally {
      setPending(null);
    }
  };

  const ignorieren = async (fid) => {
    await fetch(`${API_BASE}/ki/admin/fragen/${fid}/ignorieren`, { method: "POST" });
    await laden();
  };

  const offen = fragen.filter(f => f.status === "offen").length;

  return (
    <div className="ki-admin-seite">
      <div className="ki-admin-header">
        <div>
          <h1>👻 KI Admin</h1>
          <p className="ki-admin-sub">
            Hier siehst du Fragen die der Assistent nicht beantworten konnte.
            Deine Antworten lernt er und nutzt sie beim nächsten Mal.
          </p>
        </div>
        {offen > 0 && (
          <div className="ki-admin-badge-gross">
            <span>{offen}</span>
            <small>offen</small>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="ki-admin-tabs">
        {[
          { key: "offen",       label: "🔔 Offen" },
          { key: "beantwortet", label: "✅ Beantwortet" },
          { key: "ignoriert",   label: "🚫 Ignoriert" },
          { key: "alle",        label: "📋 Alle" },
        ].map(t => (
          <button
            key={t.key}
            className={`ki-admin-tab ${filter === t.key ? "aktiv" : ""}`}
            onClick={() => setFilter(t.key)}
          >
            {t.label}
          </button>
        ))}

        <div style={{ flex: 1 }} />

        <button
          className="ki-admin-tab wissen-btn"
          onClick={() => setWissenOffen(!wissenOffen)}
        >
          📚 Wissensbasis
        </button>
      </div>

      {/* Wissensbasis Panel */}
      {wissenOffen && (
        <div className="wissen-panel">
          <div className="wissen-panel-titel">📚 Aktueller Inhalt der Wissensbasis (wissen.md)</div>
          <pre className="wissen-inhalt">
            {wissen || "— Noch keine Einträge vorhanden —"}
          </pre>
        </div>
      )}

      {/* Fragen Liste */}
      {loading ? (
        <div className="ki-admin-laden">Lade…</div>
      ) : fragen.length === 0 ? (
        <div className="ki-admin-leer">
          {filter === "offen"
            ? "🎉 Keine offenen Fragen – der Assistent ist gut informiert!"
            : "Keine Einträge gefunden."}
        </div>
      ) : (
        <div className="ki-admin-liste">
          {fragen.map(frage => (
            <div key={frage.id} className={`ki-admin-karte ${frage.status}`}>
              <div className="ki-admin-karte-header">
                <span className={`ki-status-badge ${STATUS_LABELS[frage.status]?.cls}`}>
                  {STATUS_LABELS[frage.status]?.emoji} {STATUS_LABELS[frage.status]?.label}
                </span>
                <span className="ki-frage-ts">
                  <ZeitAgo ts={frage.timestamp} /> · ID: <code>{frage.id}</code>
                </span>
              </div>

              <div className="ki-frage-text">
                💬 „{frage.frage}"
              </div>

              {frage.antwort && (
                <div className="ki-antwort-zeige">
                  <span className="ki-antwort-label">Deine Antwort:</span>
                  <span>{frage.antwort}</span>
                </div>
              )}

              {frage.status === "offen" && (
                <div className="ki-antwort-form">
                  <textarea
                    className="ki-antwort-input"
                    placeholder="Antwort eingeben – wird in die Wissensbasis aufgenommen…"
                    value={antworten[frage.id] || ""}
                    onChange={e => setAntworten(prev => ({ ...prev, [frage.id]: e.target.value }))}
                    rows={3}
                    onKeyDown={e => {
                      if (e.key === "Enter" && e.ctrlKey) beantworten(frage.id);
                    }}
                  />
                  <div className="ki-antwort-buttons">
                    <button
                      className="ki-btn speichern"
                      disabled={!antworten[frage.id]?.trim() || pending === frage.id}
                      onClick={() => beantworten(frage.id)}
                    >
                      {pending === frage.id ? "Speichert…" : "✅ Antworten (Ctrl+Enter)"}
                    </button>
                    <button
                      className="ki-btn ignorieren"
                      onClick={() => ignorieren(frage.id)}
                    >
                      🚫 Ignorieren
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
